import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRightIcon, ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { getMyAttempts } from "../../services/Attempts/AttemptService";
import SimpleLoader from "../../components/Loader/SimpleLoader";
import { Button } from "../../components/forms/Buttons/Button";
import { getAttemptStatusText } from "../../utils/examError";
import { formatDateTime } from "../../utils/formatDate";
import { StudentTitle } from "./parts";

const STATUS_COLORS = {
  IN_PROGRESS: "bg-blue-50 text-blue-700 ring-blue-600/20",
  SUBMITTED: "bg-amber-50 text-amber-700 ring-amber-600/20",
  GRADED: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  TIMED_OUT: "bg-violet-50 text-violet-700 ring-violet-600/20",
  CANCELLED: "bg-slate-100 text-slate-600 ring-slate-500/20",
  GRADING_FAILED: "bg-red-50 text-red-700 ring-red-600/20",
};

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";

function StatusBadge({ status, t }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600 ring-slate-500/20"}`}
    >
      {getAttemptStatusText(t, status)}
    </span>
  );
}

// An attempt has an action while it runs (continue it) or once it is graded (its result).
const hasAction = (attempt) => attempt.status === "IN_PROGRESS" || attempt.status === "GRADED";

// The attempt's action: "Continue" stands out (its time is running), "View result" is a quiet link, as every
// graded row has one; the result then leads back here. 44px tall on phones, where "Continue" takes the card's width.
function AttemptAction({ attempt, t, navigate }) {
  if (attempt.status === "IN_PROGRESS") {
    return (
      <Button
        text={t("exam_taking.continue_exam")}
        color="primary"
        className="!h-11 !px-3.5 text-sm max-md:w-full md:!h-9"
        onClick={() => navigate(`/exam/attempt/${attempt.uuid}`)}
      />
    );
  }
  if (attempt.status === "GRADED") {
    return (
      <Link
        to={`/exam/attempt/${attempt.uuid}/result`}
        state={{ from: "/my-attempts" }}
        className={`-mr-2 inline-flex h-11 items-center gap-1 whitespace-nowrap rounded-lg px-2 text-sm font-medium text-blue-700 transition duration-200 hover:bg-blue-50 hover:text-blue-800 md:h-9 ${focusRing}`}
      >
        {t("exam_taking.view_result")}
        <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
      </Link>
    );
  }
  return null;
}

export default function MyAttempts() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();

  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const controllerRef = useRef(null);

  useEffect(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    let isCurrent = true;

    setLoading(true);
    getMyAttempts({
      params: { status: statusFilter || undefined },
      signal: controller.signal,
    })
      .then((data) => {
        if (!isCurrent) return;
        const results = Array.isArray(data)
          ? data
          : data?.results || data?.data || [];
        setAttempts(results);
      })
      .catch(() => {})
      .finally(() => {
        if (!isCurrent) return;
        setLoading(false);
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [statusFilter]);

  const columns = useMemo(
    () => [
      {
        Header: t("exam_taking.exam"),
        accessor: "exam_name",
        Cell: ({ row }) => (
          <span className="font-medium text-slate-900">
            {row.original.exam?.name || "—"}
          </span>
        ),
      },
      {
        Header: t("exam_taking.attempt_number"),
        accessor: "attempt_number",
        Cell: ({ value }) => <span className="tabular-nums text-slate-600">#{value}</span>,
      },
      {
        Header: t("exam_taking.status"),
        accessor: "status",
        Cell: ({ value }) => <StatusBadge status={value} t={t} />,
      },
      {
        Header: t("exam_taking.started"),
        accessor: "started_at",
        Cell: ({ value }) => (
          <span className="whitespace-nowrap text-sm tabular-nums text-slate-500">{formatDateTime(value, language) || "—"}</span>
        ),
      },
      // {
      //   Header: t("exam_taking.score"),
      //   accessor: "score_percentage",
      //   Cell: ({ row }) => {
      //     if (row.original.status !== "GRADED") return "—";
      //     return (
      //       <span className="font-medium">
      //         {row.original.score_percentage || "—"}%
      //       </span>
      //     );
      //   },
      // },
    ],
    [t, language],
  );

  return (
    <div className="w-full motion-safe:animate-fade-in">
      <StudentTitle rowClassName="mb-6">{t("exam_taking.my_attempts")}</StudentTitle>

      <div className="mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label={t("exam_taking.status")}
          className="h-11 w-full rounded-lg border-slate-300 pl-3 pr-9 text-sm text-slate-700 shadow-sm transition duration-200 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 sm:h-10 sm:w-auto"
        >
          <option value="">{t("exam_taking.all_statuses")}</option>
          <option value="IN_PROGRESS">
            {t("exam_taking.status_in_progress")}
          </option>
          <option value="SUBMITTED">{t("exam_taking.status_submitted")}</option>
          <option value="GRADED">{t("exam_taking.status_graded")}</option>
          <option value="TIMED_OUT">{t("exam_taking.status_timed_out")}</option>
        </select>
      </div>

      {loading ? (
        <SimpleLoader className="h-8 w-8" />
      ) : attempts.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
          <span className="mb-4 rounded-full bg-slate-100 p-3 text-slate-400">
            <ClipboardDocumentListIcon className="h-6 w-6" aria-hidden="true" />
          </span>
          <p className="text-sm font-medium text-slate-900">{t("exam_taking.no_attempts_yet")}</p>
        </div>
      ) : (
        <>
          {/* Phones: a card per attempt, its action in sight (a table would push it off the screen). */}
          <ul className="space-y-3 md:hidden">
            {attempts.map((attempt) => (
              <li key={attempt.uuid} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <p className="min-w-0 break-words font-medium text-slate-900">{attempt.exam?.name || "—"}</p>
                  <StatusBadge status={attempt.status} t={t} />
                </div>
                <p className="mt-1 text-sm tabular-nums text-slate-500">
                  {t("exam_taking.attempt_number")}
                  {attempt.attempt_number}
                  {attempt.started_at && ` · ${formatDateTime(attempt.started_at, language)}`}
                </p>
                {hasAction(attempt) && (
                  <div className="mt-2 flex justify-end">
                    <AttemptAction attempt={attempt} t={t} navigate={navigate} />
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm md:block">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.accessor}
                      className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"
                    >
                      {col.Header}
                    </th>
                  ))}
                  <th className="whitespace-nowrap px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("exam_taking.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {attempts.map((attempt) => (
                  <tr key={attempt.uuid} className="transition-colors duration-150 hover:bg-slate-50/80">
                    {columns.map((col) => (
                      <td key={col.accessor} className="px-6 py-4 text-sm text-slate-700">
                        {col.Cell
                          ? col.Cell({
                              value: attempt[col.accessor],
                              row: { original: attempt },
                            })
                          : attempt[col.accessor]}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-6 py-3 text-right">
                      <AttemptAction attempt={attempt} t={t} navigate={navigate} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
